import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;




#outsideClickListener = (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  // component এর ভিতরের click ignore
  if (this.contains(target)) return;

  // outside click → close
  this.#deactivate();
};


  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    document.addEventListener('click', this.#outsideClickListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();

    document.removeEventListener('click', this.#outsideClickListener);
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
activate = (event) => {
  this.dispatchEvent(new MegaMenuHoverEvent());

  if (!(event.target instanceof Element) || !this.headerComponent) return;

  let item = findMenuItem(event.target);
  if (!item || item == this.#state.activeItem) return;

  const isDefaultSlot = event.target.slot === '';

  this.dataset.overflowExpanded = (!isDefaultSlot).toString();

  const previouslyActiveItem = this.#state.activeItem;

  //  PREVIOUS ITEM RESET
  if (previouslyActiveItem) {
    previouslyActiveItem.ariaExpanded = 'false';

    const prevIcon = previouslyActiveItem.querySelector('span.dropdown_icon svg');
    if (prevIcon) {
      prevIcon.style.transform = 'rotate(0deg)';
    }
  }

  //  SET NEW ACTIVE
  this.#state.activeItem = item;
  this.ariaExpanded = 'true';
  item.ariaExpanded = 'true';

  let submenu = findSubmenu(item);
  const hasSubmenu = Boolean(submenu);

  if (!hasSubmenu && !isDefaultSlot) {
    submenu = this.overflowMenu;
  }

  if (submenu) {
    submenu.dataset.active = '';

    this.#cleanupMutationObserver();

    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
    });

    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);
  }

  let finalHeight = submenu?.offsetHeight || 0;

  if (!isDefaultSlot) {
    const overflowListHeight = this.#getOverflowListLinksHeight();

    if (hasSubmenu) {
      const overflowHeight = this.overflowMenu?.offsetHeight || 0;
      finalHeight = Math.max(overflowHeight, overflowListHeight);
    } else {
      finalHeight = overflowListHeight;
    }
  }

  if (!submenu) {
    finalHeight = 0;
  }

  this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
  this.#setFullOpenHeaderHeight(finalHeight);
  this.style.setProperty('--submenu-opacity', '1');

  //  CURRENT ICON ROTATE
  const icon = item.querySelector('span.dropdown_icon svg');
  if (icon) {
    icon.style.transform = 'rotate(180deg)';
  }

  //  overflow slot active class
  const overflowSlot = this.overflowMenu?.querySelector('slot');
  if (overflowSlot) {
    const slottedElements = overflowSlot.assignedElements({ flatten: true });
    slottedElements.forEach(el => {
      const link = el.querySelector('.menu-list__link');
      if (link) {
        link.classList.add('slot-active');
      }
    });
  }
};



  toggleMenu = (event) => {
  event.preventDefault();

  const item = findMenuItem(event.target);

  if (!item) return;

  // jodi already active thake → close
  if (this.#state.activeItem === item) {
    this.#deactivate(item);
    return;
  }

  // na hole → open
  this.activate(event);
 };





toggleSubMenu = (event) => {
  event.preventDefault();

  const target = event.target;


  const icon = target.querySelector('.dropdown_icon svg');
  if (!icon) return;

  const list = target.querySelector('.list-unstyled');
  if (!list) return;


  const isRotated = icon.style.transform === 'rotate(180deg)';
  icon.style.transform = isRotated ? 'rotate(0deg)' : 'rotate(180deg)';

 
  list.style.display = list.style.display === 'block' ? 'none' : 'block';
 };




  clicktoilnk = (e) => {
    const a = e.target.closest('a');
    if (a) window.location.href = a.href;
  };

// slot-active class বসানোর জন্য helper function
markOverflowLinksActive = () => {
  const overflowSlot = this.overflowMenu?.querySelector('slot');
  if (!overflowSlot) return;

  const slottedElements = overflowSlot.assignedElements({flatten: true});
  slottedElements.forEach(el => {
    const link = el.querySelector('.menu-list__link');
    if (link) {
      link.classList.add('slot-active');
    }
  });
};

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {

    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
    
    const icon = item?.querySelector('span.dropdown_icon svg');
      if (icon) {
        icon.style.transform = 'rotate(0deg)';
      }


  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }



  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}


